// Sanningskälla för Prisindex filmproduktion Sverige 2026.
// Läses av /branschstudier/prisindex-2026 (sidan), /api/public/prisindex-2026.csv
// (CSV-nedladdning) och /api/public/branschstats (JSON). Ändra siffror HÄR —
// aldrig i vyn — så att sida, CSV och JSON aldrig kan säga olika saker.

export const PRISINDEX_PATH = "/branschstudier/prisindex-2026";
export const PRISINDEX_PUBLISHED = "2026-08-17";
export const PRISINDEX_TITLE = "Prisindex filmproduktion Sverige 2026";
export const PRISINDEX_LICENSE = "CC BY 4.0";
export const PRISINDEX_LICENSE_URL =
  "https://creativecommons.org/licenses/by/4.0/";

/** Metodfakta — journalister länkar inte till siffror utan metod. */
export const PRISINDEX_METHOD = {
  sampleProductions: 214,
  sampleQuotes: 96,
  period: "januari 2024 – juni 2026",
  percentile: "10:e–90:e percentilen",
  marginOfError: "±8 procent på formatspann, ±4 indexenheter på ortsindex",
  excludes:
    "mediaköp, musiklicenser, röstlicenser och resor utanför den angivna orten",
  referenceCity: "Göteborg = index 100",
} as const;

export interface FormatRow {
  format: string;
  length: string;
  min: number;
  max: number;
  note: string;
}

export const FORMAT_INDEX: FormatRow[] = [
  { format: "Företagsfilm", length: "2–3 min", min: 45000, max: 120000, note: "Vanligaste B2B-formatet" },
  { format: "Reklamfilm", length: "15–60 s", min: 60000, max: 350000, note: "Spannet styrs av produktionsvärde" },
  { format: "Produktfilm", length: "20–40 s", min: 35000, max: 95000, note: "Per produkt, rabatt i serie" },
  { format: "Employer branding-film", length: "1–3 min", min: 45000, max: 150000, note: "Ofta flera intervjuer" },
  { format: "Kundcase / testimonial", length: "1–2 min", min: 25000, max: 75000, note: "En inspelningsdag" },
  { format: "Animerad förklaringsfilm", length: "60–90 s", min: 40000, max: 140000, note: "Manus + design + animation" },
  { format: "Social cutdown", length: "6–15 s", min: 3000, max: 12000, note: "Per cut, vid befintligt råmaterial" },
  { format: "Heritage / kunskapsarv", length: "20–60 min", min: 60000, max: 250000, note: "Flera inspelningstillfällen" },
  { format: "Tv-reklam (produktion)", length: "15–30 s", min: 150000, max: 450000, note: "Exkl. mediaköp, sändningsklar leverans" },
  { format: "Versionering till tv", length: "10–30 s", min: 15000, max: 40000, note: "Nedklipp av befintlig film, EBU R128-mix" },
  { format: "Eventfilm", length: "1 dag", min: 30000, max: 250000, note: "Antal kameror och scenljud styr spannet" },
  { format: "3D-animation", length: "30–60 s", min: 80000, max: 300000, note: "Modellering och rendering dominerar kostnaden" },
];

export interface RoleRow {
  role: string;
  min: number;
  max: number;
}

export const ROLE_INDEX: RoleRow[] = [
  { role: "Regissör / producent", min: 9000, max: 16000 },
  { role: "Fotograf med kamerapaket", min: 10000, max: 18000 },
  { role: "Ljudtekniker", min: 6000, max: 10000 },
  { role: "Klippare", min: 6500, max: 11000 },
  { role: "Motion designer", min: 7000, max: 13000 },
  { role: "Colorist", min: 8000, max: 14000 },
];

export interface CityRow {
  city: string;
  index: number;
  note: string;
}

export const CITY_INDEX: CityRow[] = [
  { city: "Stockholm", index: 112, note: "Högst studiokostnad och teamarvoden" },
  { city: "Göteborg", index: 100, note: "Referensnivå i indexet" },
  { city: "Malmö", index: 95, note: "Lägre studiokostnad, kort restid till Köpenhamn" },
  { city: "Uppsala", index: 97, note: "Nära Stockholms-team utan Stockholmspåslag" },
  { city: "Umeå / Norrland", index: 104, note: "Färre lokala team, restidspåslag" },
];

export const KEY_STATS: { text: string; about: string }[] = [
  {
    text: "En företagsfilm på 2–3 minuter kostar 45 000–120 000 kr i Sverige 2026, exklusive mediaköp.",
    about: "Prisspann företagsfilm Sverige 2026",
  },
  {
    text: "Dagspriset för ett tvåmannateam med regi, foto och ljud ligger på 18 000–32 000 kr 2026.",
    about: "Dagspris filmteam Sverige 2026",
  },
  {
    text: "Filmproduktion i Stockholm prissätts i median 12 procent högre än i Göteborg och 18 procent högre än i Malmö vid identisk omfattning.",
    about: "Regional prisskillnad filmproduktion Sverige",
  },
  {
    text: "Abonnemangsmodell blir billigare per levererad film vid fler än tre filmer per år, med brytpunkt runt 14 995 kr per månad.",
    about: "Brytpunkt abonnemang vs projektpris filmproduktion",
  },
  {
    text: "Mediantiden från godkänd brief till levererad film är 10 arbetsdagar med abonnemang och 4–6 veckor i traditionell projektmodell.",
    about: "Leveranstid filmproduktion Sverige 2026",
  },
];

const sek = (n: number) => n.toLocaleString("sv-SE");

/** Visningsformat för prisspann, används av sidan. */
export const range = (min: number, max: number) => `${sek(min)}–${sek(max)} kr`;

/** Hela indexet som CSV (semikolon = svensk Excel-standard). */
export function prisindexCsv(): string {
  const rows: string[][] = [
    ["kategori", "post", "detalj", "min_sek", "max_sek", "kommentar"],
    ...FORMAT_INDEX.map((f) => [
      "format",
      f.format,
      f.length,
      String(f.min),
      String(f.max),
      f.note,
    ]),
    ...ROLE_INDEX.map((r) => [
      "dagspris_roll",
      r.role,
      "per dag",
      String(r.min),
      String(r.max),
      "",
    ]),
    ...CITY_INDEX.map((c) => [
      "ortsindex",
      c.city,
      "index (Göteborg=100)",
      String(c.index),
      String(c.index),
      c.note,
    ]),
  ];

  const esc = (v: string) =>
    /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

  const header = [
    `# ${PRISINDEX_TITLE}`,
    `# Källa: Fenny — https://fenny.se${PRISINDEX_PATH}`,
    `# Licens: ${PRISINDEX_LICENSE} (${PRISINDEX_LICENSE_URL})`,
    `# Publicerad: ${PRISINDEX_PUBLISHED} · Urval: ${PRISINDEX_METHOD.sampleProductions} produktioner och ${PRISINDEX_METHOD.sampleQuotes} offertunderlag, ${PRISINDEX_METHOD.period}`,
    `# Spann: ${PRISINDEX_METHOD.percentile} · Felmarginal: ${PRISINDEX_METHOD.marginOfError}`,
  ].join("\n");

  return `${header}\n${rows.map((r) => r.map(esc).join(";")).join("\n")}\n`;
}
